import React, { useCallback, useEffect, useState } from "react";
import { ListChecks, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { useAuth } from "../context/AuthContext";
import { ChecklistCard } from "../components/checklist/ChecklistCard";
import { ShareModal } from "../components/checklist/ShareModal";
import ConfirmModal from "../components/ConfirmModal";

/**
 * Pagina Checklist — liste personali libere.
 * "Le mie liste" + "Condivise con me". Creazione inline (titolo + Invio).
 */
export default function Checklist() {
  const { user } = useAuth();
  const [data, setData] = useState({ mine: [], shared: [], pinned_checklist_id: null });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [busyCreate, setBusyCreate] = useState(false);
  const [shareFor, setShareFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [busyDelete, setBusyDelete] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/checklists");
      setData(data);
      setForbidden(false);
      setLoadFailed(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        setLoadFailed(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createList = async (e) => {
    e?.preventDefault();
    const titolo = newTitle.trim();
    if (!titolo || busyCreate) return;
    setBusyCreate(true);
    try {
      await api.post("/checklists", { titolo });
      setNewTitle("");
      setCreating(false);
      toast.success("Lista creata");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella creazione");
    } finally {
      setBusyCreate(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteFor) return;
    setBusyDelete(true);
    try {
      await api.delete(`/checklists/${deleteFor.id}`);
      toast.success(`Lista «${deleteFor.titolo}» eliminata`);
      setDeleteFor(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setBusyDelete(false);
    }
  };

  const isEmpty = !loading && data.mine.length === 0 && data.shared.length === 0;

  if (forbidden) {
    return (
      <div className="wl-page" data-testid="checklist-page">
        <div>
          <div className="wl-page__eyebrow">Operatività</div>
          <h1 className="wl-page__title">Checklist</h1>
        </div>
        <div className="wl-card" data-testid="checklist-forbidden-state">
          <div className="wl-card__body text-center py-12">
            <div className="w-12 h-12 rounded-full bg-subtle flex items-center justify-center mx-auto mb-4">
              <ListChecks size={22} strokeWidth={1.5} className="text-text-muted" />
            </div>
            <div className="text-base font-medium text-text-primary mb-1">Modulo non attivo</div>
            <div className="text-sm text-text-secondary max-w-md mx-auto">
              Il modulo Checklist non è abilitato per la tua organizzazione.
              Contatta l'amministratore di Wattline per attivarlo.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wl-page" data-testid="checklist-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="wl-page__eyebrow">Operatività</div>
          <h1 className="wl-page__title">Checklist</h1>
          <p className="wl-page__meta">Liste personali libere: crea le tue e decidi se e con chi condividerle.</p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="wl-btn wl-btn--primary"
          data-testid="new-checklist-btn"
        >
          <Plus size={15} /> Nuova lista
        </button>
      </div>

      {/* Creazione inline */}
      {creating && (
        <form onSubmit={createList} className="wl-card" data-testid="new-checklist-form">
          <div className="wl-card__body flex items-center gap-3">
            <ListChecks size={18} className="text-accent shrink-0" />
            <input
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { setCreating(false); setNewTitle(""); } }}
              placeholder="Titolo della lista (es. Preparazione audit) — premi Invio"
              className="flex-1 text-sm bg-transparent outline-none text-text-primary placeholder:text-text-muted"
              data-testid="new-checklist-title"
            />
            <button type="submit" disabled={busyCreate || !newTitle.trim()} className="wl-btn wl-btn--primary wl-btn--sm" data-testid="new-checklist-submit">
              {busyCreate ? "…" : "Crea"}
            </button>
            <button type="button" onClick={() => { setCreating(false); setNewTitle(""); }} className="wl-btn wl-btn--ghost wl-btn--sm">
              Annulla
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-text-muted py-8">
          <Loader2 size={16} className="animate-spin" /> Caricamento checklist…
        </div>
      ) : loadFailed ? (
        <div className="wl-card"><LoadErrorBox what="le checklist" onRetry={load} testId="checklist-load-error" /></div>
      ) : isEmpty ? (
        /* Stato vuoto parlante */
        <div className="wl-card" data-testid="checklist-empty-state">
          <div className="wl-card__body text-center py-12">
            <div className="w-12 h-12 rounded-full bg-subtle flex items-center justify-center mx-auto mb-4">
              <ListChecks size={22} strokeWidth={1.5} className="text-text-muted" />
            </div>
            <div className="text-base font-medium text-text-primary mb-1">Nessuna checklist ancora</div>
            <div className="text-sm text-text-secondary max-w-md mx-auto mb-5">
              Una lista per l'audit, una per l'onboarding di un cliente, una per le cose
              da non dimenticare venerdì: le checklist ti aiutano a non perdere pezzi.
            </div>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="wl-btn wl-btn--primary"
              data-testid="empty-create-btn"
            >
              <Plus size={15} /> Crea la tua prima checklist
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Le mie liste */}
          <section data-testid="my-lists-section">
            <h2 className="text-base font-semibold text-text-primary mb-3">Le mie liste</h2>
            {data.mine.length === 0 ? (
              <div className="text-sm text-text-muted mb-2">Non hai ancora creato liste tue.</div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {data.mine.map((cl) => (
                  <ChecklistCard
                    key={cl.id}
                    list={cl}
                    currentUser={user}
                    pinnedId={data.pinned_checklist_id}
                    onReload={load}
                    onAskDelete={setDeleteFor}
                    onOpenShare={setShareFor}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Condivise con me */}
          {data.shared.length > 0 && (
            <section data-testid="shared-lists-section">
              <h2 className="text-base font-semibold text-text-primary mb-3">Condivise con me</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {data.shared.map((cl) => (
                  <ChecklistCard
                    key={cl.id}
                    list={cl}
                    currentUser={user}
                    pinnedId={data.pinned_checklist_id}
                    onReload={load}
                    onAskDelete={setDeleteFor}
                    onOpenShare={setShareFor}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {shareFor && (
        <ShareModal list={shareFor} onClose={() => setShareFor(null)} onSaved={load} />
      )}

      <ConfirmModal
        open={!!deleteFor}
        onOpenChange={(o) => !o && setDeleteFor(null)}
        title={`Eliminare «${deleteFor?.titolo}»?`}
        description="La lista e tutte le sue voci verranno eliminate definitivamente, anche per le persone con cui è condivisa."
        confirmLabel="Elimina lista"
        variant="danger"
        loading={busyDelete}
        onConfirm={confirmDelete}
        testId="delete-checklist-modal"
      />
    </div>
  );
}
